import { useQuery } from "@tanstack/react-query"
import { Button } from "@workspace/web-ui/components/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@workspace/web-ui/components/dialog"
import { Skeleton } from "@workspace/web-ui/components/skeleton"
import { cn } from "@workspace/web-ui/lib/utils"
import { Info, Loader2 } from "lucide-react"
import { toast } from "sonner"
import { useApproveRefillMutation, useRejectRefillMutation } from "../../api/refills"
import { getRefillsByIdQueryOptions } from "../../api/refills/query"
import { PatientCard } from "./patient-card"
import { RefillCard } from "./refill-card"
import { useState } from "react"
import { getRouteApi } from "@tanstack/react-router"
import { useSendInvoiceMutation } from "../../pages/encounters/api/mutations"
import DosespotButton from "../../pages/patient-logs/pages/dosespotModal"
import { formatDate } from "~/utils"
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@workspace/web-ui/components/select"

interface RefillProps {
    refillId: string
    open: boolean
    onOpenChange: (open: boolean) => void
    urgent_care_onboarded?: boolean
    encounterId?: string
    sessionId?: string
    userDosespotId?: string
}

export const MedicalRefillsComponent = ({ refillId, open, onOpenChange, urgent_care_onboarded, encounterId, sessionId, userDosespotId }: RefillProps) => {

    const approveRefillMutation = useApproveRefillMutation({
        refill_id: refillId
    })

    const rejectRefillMutation = useRejectRefillMutation()

    const sendInvoiceMutation = useSendInvoiceMutation()

    const { data, isLoading } = useQuery(getRefillsByIdQueryOptions({
        id: refillId
    }))

    const routeApi = getRouteApi("/(dashboard)/dashboard/$program");
    const { program } = routeApi.useParams();

    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [reasonError, setReasonError] = useState("");
    const [instructions, setInstructions] = useState("");
    const [rejectDetails, setRejectDetails] = useState("");
    const handleApproveRefill = async () => {
        try {
            const response = await approveRefillMutation.mutateAsync();

            if (response.status === "success") {
                // If it's weight-loss program and user has paid for the refill, send invoice to pharmacy
                if (program === "weight-loss" && data && data.paymentStatus === "paid") {
                    try {
                        await sendInvoiceMutation.mutateAsync({
                            patient_id: data.patientId,
                            type_id: refillId,
                            type: "Refill"
                        });
                    } catch (invoiceError) {
                        console.error("Error sending invoice to pharmacy:", invoiceError);
                        // Don't fail the whole operation if invoice sending fails
                    }
                }

                toast.success('Refill Request Approved Successfully', {
                    style: {
                        background: "#10B981",
                        color: "#FFFFFF",
                        borderColor: "#10B981",
                        '& svg': {
                            color: '#FFFFFF'
                        }
                    } as React.CSSProperties
                });

                onOpenChange(false);
            }
        } catch (error) {
            console.error("Error approving refill request:", error);
            return toast.error(
                'Failed to approve refill request.',
                {
                    style: {
                        background: '#DC2626',
                        color: '#FFFFFF',
                        borderColor: '#DC2626',
                        '& svg': {
                            color: '#FFFFFF'
                        }
                    } as React.CSSProperties
                }
            );
        }
    }


    const handleRejectRefill = async (reason: string, details: string) => {
        try {
            const response = await rejectRefillMutation.mutateAsync({
                refill_id: refillId,
                reason,
                details,
            });

            if (response.status === "success") {
                toast.success('Refill Request Rejected Successfully', {
                    style: {
                        background: "#10B981",
                        color: "#FFFFFF",
                        borderColor: "#10B981",
                        '& svg': {
                            color: '#FFFFFF'
                        }
                    } as React.CSSProperties
                });

                onOpenChange(false);
            }
        } catch (error) {
            console.error("Error rejecting refill request:", error);
            return toast.error(
                'Failed to reject refill request.',
                {
                    style: {
                        background: '#DC2626',
                        color: '#FFFFFF',
                        borderColor: '#DC2626',
                        '& svg': {
                            color: '#FFFFFF'
                        }
                    } as React.CSSProperties
                }
            );
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}
        >
            <DialogContent
                className={cn(
                    "flex flex-col h-full gap-0 py-2 px-4 sm:max-h-[min(640px,90vh)]",
                    "focus:outline-none"
                )}
                aria-labelledby="refill-modal-title"
                data-testid="refill-modal"
            >
                <DialogHeader>
                    <DialogTitle className="text-2xl font-semibold mb-4">
                        Medication Refill
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-2 flex flex-col overflow-y-auto hide-scrollbar">
                    {isLoading ? (
                        <div className="space-y-4">
                            {/* PatientCard Skeleton */}
                            <div className="p-4 border rounded-lg flex flex-col gap-2">
                                <Skeleton className="h-6 w-1/3 mb-2" /> {/* Patient Name */}
                                <Skeleton className="h-4 w-1/2" /> {/* Program Enrollment */}
                                <Skeleton className="h-4 w-1/4" /> {/* Request Date */}
                                <Skeleton className="h-4 w-1/4" /> {/* Current Weight */}
                                <Skeleton className="h-4 w-1/4" /> {/* Patient ID */}
                            </div>
                            {/* RefillCard Skeleton */}
                            <div className="p-4 border rounded-lg flex flex-col gap-2">
                                <Skeleton className="h-6 w-1/2 mb-2" /> {/* Medication Name */}
                                <Skeleton className="h-4 w-1/3" /> {/* Dosage */}
                                <Skeleton className="h-4 w-1/3" /> {/* Refills */}
                            </div>
                            {/* Button Row Skeleton */}
                            <div className="flex justify-end gap-2 p-6 ml-auto">
                                <Skeleton className="h-10 w-32 rounded-md" /> {/* Approve Button */}
                                <Skeleton className="h-10 w-32 rounded-md" /> {/* Reject Button */}
                            </div>
                        </div>
                    ) : (
                        <>
                            {data && (
                                <>
                                    <PatientCard
                                        patientName={data.patientName}
                                        patientEmail={data.patientEmail}
                                        programEnrollment={data.programEnrollment}
                                        requestDate={`${formatDate(data.requestedAt)}`}
                                        currentWeight={`${data.weightHistory.currentWeight} lbs`}
                                        patientId={data.patientId}
                                    />

                                    {!Array.isArray(data.medications) && (
                                        <RefillCard
                                            medicationName={data.medications.name || ""}
                                            medicationDosage={data.medications.dosage || ""}
                                            medicationQuantity={data.medications.refills?.toString() || data.medications.quantity?.toString() || ''}
                                            medicationVitamin={data.medications.vitamin || ""}
                                        />
                                    )}
                                    {program === "weight-loss" && (
                                        <div className="flex flex-col gap-2 mt-4">
                                            <label htmlFor="refill-instructions" className="text-[14px] text-[#1A202C]">
                                                Instructions for medication
                                            </label>
                                            <textarea
                                                id="refill-instructions"
                                                value={instructions}
                                                onChange={e => setInstructions(e.target.value)}
                                                maxLength={500}
                                                className="w-full rounded-[8px] border outline-none h-[81px] p-2 placeholder:text-[14px]"
                                                placeholder="Provide instructions for the patient on how to take their medication."
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                            {/* Action footer moved below to be fixed at bottom */}
                        </>
                    )}
                </div>

                <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
                    <DialogContent className="rounded-[24px]">
                        <DialogHeader className="flex items-center justify-center">
                            <DialogTitle className=" text-xl font-medium">Reject Request</DialogTitle>
                        </DialogHeader>
                        <div>
                            <p className="flex items-center mb-4 text-xl font-medium justify-center text-center">
                                You are rejecting the refill request for {(() => {
                                    if (!data?.medications || Array.isArray(data.medications)) return '';
                                    const med = data.medications;
                                    return `${med.name || ''} ${med.dosage || ''}`;
                                })()} for {data?.patientName}.
                            </p>
                            <label className="text-[14px] mt-4 text-[#1A202C]">
                            <Select value={rejectReason} onValueChange={setRejectReason}>
                              <SelectTrigger className="w-full rounded-[8px] border h-[44px] p-2 mt-2 mb-4">
                                <SelectValue placeholder="Select a reason" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectGroup>
                                  <SelectItem value="Cannot prescribe this medication">Cannot prescribe this medication</SelectItem>
                                  {program === "urgent-care" && (
                                            <SelectItem value="Need more information">Need more information</SelectItem>
                                  )}
                                  {program === "weight-loss" && (
                                    <SelectItem value="Medication Completed / Discontinued">Medication Completed / Discontinued</SelectItem>
                                  )}
                                </SelectGroup>
                              </SelectContent>
                            </Select>
                            <label className="text-[14px] mt-4 text-[#1A202C]">Please provide additional details:</label>
                            <textarea
                                value={rejectDetails}
                                onChange={e => setRejectDetails(e.target.value)}
                                    maxLength={200}
                                    className="w-full rounded-[8px] border h-[164px] p-2 mt-2"
                                />
                            </label>
                            {reasonError && <div className="text-red-500">{reasonError}</div>}
                            <div className="flex justify-end gap-2 mt-4">
                                <Button onClick={() => setShowRejectModal(false)} className="bg-transparent hover:bg-transparent border border-[#DCDCDE] rounded-[8px] text-[#202020CC]">Cancel</Button>
                                <Button
                                    className="!bg-[#E12121] rounded-[8px] text-white"
                                    disabled={rejectRefillMutation.isPending}
                                    onClick={async () => {
                                        if (!rejectReason.trim()) {
                                            setReasonError("Reason is required.");
                                            return;
                                        }
                                        if (!rejectDetails.trim()) {
                                            setReasonError("Additional details are required.");
                                            return;
                                        }
                                        setReasonError("");
                                        await handleRejectRefill(rejectReason, rejectDetails);
                                        setShowRejectModal(false);
                                    }}
                                >
                                    {rejectRefillMutation.isPending ? (
                                        <Loader2 className="w-4 h-4 text-white animate-spin" />
                                    ) : (
                                        "Confirm"
                                    )}
                                </Button>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
                {/* Fixed bottom action footer via flex layout */}
                <div className="mt-auto bg-white py-4 border-t">
                    {program === "weight-loss" && data?.paymentStatus !== "paid" && (
                        <div className="text-sm text-[#1A202C] bg-[#EBF0FB] p-2 rounded-md flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <Info className="text-[#4272DD] size-[16px]" />
                                <span>Payment required before sending to pharmacy.</span>
                            </div>
                            <Button
                                className="!bg-[#2563EB] !text-white !rounded-[6px] !py-1 !px-3 !text-xs"
                                onClick={() => {
                                    toast.info('Payment reminder sent to patient');
                                }}
                            >
                                Remind to Pay
                            </Button>
                        </div>
                    )}
                    <div className="flex gap-2">
                        <Button
                            className={`w-[133px] !bg-[#E12121] !py-[12px] px-[16px] ${program=== "urgent-care" ? "h-[44px] rounded" : "!rounded-[8px]"}`}
                            onClick={() => setShowRejectModal(true)}
                        >
                            Reject Refill
                        </Button>
                        {program === "urgent-care" ? (
                            <div className="flex-1">
                                <DosespotButton
                                    userDosespotId={userDosespotId}
                                    sessionId={sessionId}
                                    encounterId={encounterId}
                                    urgent_care_onboarded={urgent_care_onboarded}
                                    onSuccess={() => {}}
                                />
                            </div>
                        ) : (
                            <Button
                                className={cn(
                                    "!text-white w-full !rounded-[8px] !border-none !py-[14px]",
                                    program === "weight-loss" && data?.paymentStatus !== "paid" && "!cursor-not-allowed"
                                )}
                                onClick={handleApproveRefill}
                                disabled={program === "weight-loss" && data?.paymentStatus !== "paid"}
                            >
                                {(approveRefillMutation.isPending || sendInvoiceMutation.isPending) ? (
                                    <Loader2 className="w-4 h-4 text-white" />
                                ) : (
                                    <>
                                        {program === "weight-loss" ? "Send to Pharmacy" : "Approve Refill"}
                                    </>
                                )}
                            </Button>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}


